/** Flow 6 — Suivi des activités (simulation, no backend) */

export const FLOW6_TITLE = "Flow 6 — Suivi des activités";

export const FLOW6_STEPS = [
  "Demande de retour après la séance",
  "L'élève indique ce qu'il a fait",
  "Temps consacré",
  "Niveau de difficulté ou blocage",
  "Enregistrement et replanification",
];

export const FLOW6_CANON = {
  activities: ["J'ai révisé la théorie", "J'ai fait des exercices"],
  activitiesText: "J'ai révisé la théorie et fait des exercices.",
  activitiesSummary: "Théorie + exercices",
  duration: "45 min",
  difficulty: "Plutôt facile",
  blockage: "Un peu difficile sur les fractions, je n'étais pas sûr de la méthode.",
  blockageShort: "Fractions (méthode)",
};

export const ACTIVITY_OPTIONS = [
  "J'ai révisé la théorie",
  "J'ai fait des exercices",
  "J'ai corrigé des erreurs",
  "J'ai travaillé un devoir",
  "Autre",
];

export const TIME_OPTIONS = ["15 min", "30 min", "45 min", "1 h", "Plus de 1 h"];

export const DIFFICULTY_OPTIONS = [
  "Facile",
  "Plutôt facile",
  "Moyenne",
  "Difficile",
  "Très difficile",
];

export const ASK_MESSAGE =
  "Comment s'est passée ta séance de maths ? Tu peux me dire ce que tu as fait, le temps que tu y as consacré et si tu as rencontré des difficultés.";

export const LATER_MESSAGE =
  "Pas de souci. Quand tu voudras, appuie sur « Je fais un retour maintenant ».";

export const ALREADY_MESSAGE =
  "Parfait, je note que tu as déjà fait ton retour. On se reparle à la prochaine séance.";

export const ACTIVITIES_MESSAGE =
  "Parfait ! Qu'as-tu fait pendant ta séance ? Sélectionne une ou plusieurs activités.";

export const DURATION_MESSAGE =
  "Combien de temps as-tu travaillé ? Indique le temps consacré à cette séance.";

export const DIFFICULTY_MESSAGE =
  "Comment évalues-tu la difficulté de cette séance ?";

export const BLOCKAGE_MESSAGE =
  "Tu as rencontré un blocage ou un point particulier ?";

export const ADAPT_MESSAGE =
  "Souhaites-tu que j'adapte le reste de ton plan ? Je peux ajouter une séance de révision sur les fractions cette semaine si tu le souhaites.";

export const ADAPTED_MESSAGE =
  "C'est noté. J'ajoute une séance de révision sur les fractions cette semaine.";

export const KEEP_MESSAGE =
  "Parfait. Ton plan reste comme il est. À la prochaine séance !";

export function emptySession() {
  return {
    activities: [],
    duration: "",
    difficulty: "",
    blockage: "",
  };
}

export function flow6DisplayIndex(flowStep) {
  if (flowStep <= 0 || flowStep >= 90) return 0;
  if (flowStep === 1) return 1;
  if (flowStep === 2) return 2;
  if (flowStep <= 4) return 3;
  return 4;
}

export function sessionMinutes(value) {
  if (value === "15 min") return 15;
  if (value === "30 min") return 30;
  if (value === "45 min") return 45;
  if (value === "1 h") return 60;
  if (value === "Plus de 1 h") return 75;
  return 0;
}

function slug(label) {
  return label
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function choiceReplies(prefix, options, primaryLabel) {
  return options.map((label) => ({
    id: `${prefix}${slug(label)}`,
    label,
    primary: label === primaryLabel,
  }));
}

export function activitiesSentence(labels) {
  const picked = labels.length ? labels : FLOW6_CANON.activities;
  if (
    picked.length === FLOW6_CANON.activities.length &&
    FLOW6_CANON.activities.every((item) => picked.includes(item))
  ) {
    return FLOW6_CANON.activitiesText;
  }
  if (picked.length === 1) return `${picked[0]}.`;
  const rest = picked
    .slice(1)
    .map((item) => item.replace(/^J'ai\s+/i, ""))
    .join(" et ");
  return `${picked[0]} et ${rest}.`;
}

export function activitiesSummary(labels) {
  const picked = labels.length ? labels : FLOW6_CANON.activities;
  if (
    picked.length === 2 &&
    picked.some((item) => /théorie/i.test(item)) &&
    picked.some((item) => /exercices/i.test(item))
  ) {
    return FLOW6_CANON.activitiesSummary;
  }
  return picked.map((item) => item.replace(/^J'ai\s+/i, "")).join(" + ");
}

export function blockageShort(text) {
  if (!text) return "—";
  if (/fraction/i.test(text)) return FLOW6_CANON.blockageShort;
  return text.length > 42 ? `${text.slice(0, 40)}…` : text;
}

export const FLOW6_REPLIES = {
  ask: [
    { id: "f6-now", label: "Je fais un retour maintenant", primary: true },
    { id: "f6-later", label: "Un peu plus tard", primary: false },
    { id: "f6-done", label: "Je l'ai déjà fait", primary: false },
  ],
  resume: [{ id: "f6-now", label: "Je fais un retour maintenant", primary: true }],
  bridge: [{ id: "f6-start", label: "Faire le retour de séance", primary: true }],
  blockage: [{ id: "f6-block", label: FLOW6_CANON.blockage, primary: true }],
  adapt: [
    { id: "f6-yes", label: "Oui, adapte mon plan", primary: true },
    { id: "f6-no", label: "Non, tout va bien", primary: false },
  ],
};

export function activityReplies(selected = []) {
  const picks = selected.length ? selected : [];
  return [
    ...ACTIVITY_OPTIONS.map((label) => ({
      id: `f6-act-${slug(label)}`,
      label,
      primary: false,
      selected: picks.includes(label),
    })),
    {
      id: "f6-acts-ok",
      label: picks.length ? "Valider" : FLOW6_CANON.activitiesText,
      primary: true,
    },
  ];
}

export function durationReplies() {
  return choiceReplies("f6-time-", TIME_OPTIONS, FLOW6_CANON.duration);
}

export function difficultyReplies() {
  return choiceReplies("f6-diff-", DIFFICULTY_OPTIONS, FLOW6_CANON.difficulty);
}

export function labelForActivity(id) {
  return ACTIVITY_OPTIONS.find((label) => `f6-act-${slug(label)}` === id) || "";
}

export function labelForTime(id) {
  return TIME_OPTIONS.find((label) => `f6-time-${slug(label)}` === id) || "";
}

export function labelForDifficulty(id) {
  return DIFFICULTY_OPTIONS.find((label) => `f6-diff-${slug(label)}` === id) || "";
}

function result(messages, quickReplies, flowStep, session, planReady = false) {
  return {
    messages,
    quickReplies,
    flowStep,
    status: FLOW6_STEPS[flow6DisplayIndex(flowStep)],
    session,
    planReady,
  };
}

/** Instant history for a sommaire step (display index 0–4). */
export function buildFlow6Snapshot(displayIndex) {
  const step = Math.min(Math.max(displayIndex, 0), FLOW6_STEPS.length - 1);
  const session = emptySession();
  const messages = [{ sender: "agent", messageKey: "f6.ask", text: ASK_MESSAGE }];
  if (step === 0) return result(messages, FLOW6_REPLIES.ask, 0, session);

  messages.push({ sender: "user", text: "Je fais un retour maintenant" });
  messages.push({ sender: "agent", messageKey: "f6.activities", text: ACTIVITIES_MESSAGE });
  if (step === 1) return result(messages, activityReplies(), 1, session);

  session.activities = [...FLOW6_CANON.activities];
  messages.push({ sender: "user", text: FLOW6_CANON.activitiesText });
  messages.push({ sender: "agent", messageKey: "f6.duration", text: DURATION_MESSAGE });
  if (step === 2) return result(messages, durationReplies(), 2, session);

  session.duration = FLOW6_CANON.duration;
  messages.push({ sender: "user", text: FLOW6_CANON.duration });
  messages.push({ sender: "agent", messageKey: "f6.difficulty", text: DIFFICULTY_MESSAGE });
  if (step === 3) return result(messages, difficultyReplies(), 3, session);

  session.difficulty = FLOW6_CANON.difficulty;
  session.blockage = FLOW6_CANON.blockage;
  messages.push({ sender: "user", text: FLOW6_CANON.difficulty });
  messages.push({ sender: "agent", messageKey: "f6.blockage", text: BLOCKAGE_MESSAGE });
  messages.push({ sender: "user", text: FLOW6_CANON.blockage });
  messages.push({
    sender: "agent",
    kind: "session",
    session: { ...session, activities: [...session.activities] },
  });
  messages.push({ sender: "agent", messageKey: "f6.adapt", text: ADAPT_MESSAGE });
  return result(messages, FLOW6_REPLIES.adapt, 5, session, true);
}
